import { useEffect, useRef, useState } from "react";
import { parseFactoryLines } from "utils/factoryTest";
import { foldFeedRows, impactPeakG } from "utils/liveFeed";

const PULSE_CAP = 48;

/**
 * Modal-level live feed state: the structured activity rows, the impact pulse
 * strip, the last status beacon, the session clock, and the end-of-session
 * summary. Lives in WearableOpsModal (not LiveDeviceView) so the feed cursor
 * survives tab switches and flow entry/exit — the view used to remount, rescan
 * the WHOLE receivedData buffer from zero, and replay hours of history as if
 * it had just happened.
 *
 * Scans incrementally with its own cursor (self-healing across replugs, where
 * receivedData restarts at zero) and folds every event on a line via
 * parseFactoryLines, so glued events aren't dropped.
 */
export default function useLiveFeed({ receivedData, connected, devRecords }) {
  const [feed, setFeed] = useState([]); // newest first, impact-grouped
  const [pulse, setPulse] = useState([]); // impact peaks (g), oldest → newest
  const [beacon, setBeacon] = useState(null); // last 0x12 counters
  const [sessionStart, setSessionStart] = useState(null);
  const [sessionSummary, setSessionSummary] = useState(null);
  // What the arming phase is actually doing (grace countdown, held open by a
  // BLE link, waiting for a schedule) so the hero can narrate it instead of
  // showing a bare "Arming" for minutes.
  const [armingInfo, setArmingInfo] = useState(null);
  // Store-erase progress (v0.5.86 bucketed events; all erase paths report —
  // USB, BLE confirm/discard, pre-capture drive-to-done).
  const [eraseProgress, setEraseProgress] = useState(null);
  const scanRef = useRef(0);

  // Session bookkeeping for the summary card: records on device and peak seen
  // while the session ran.
  const sessionMetaRef = useRef(null); // { start, startRecords, peakG }
  const devRecordsRef = useRef(devRecords);
  useEffect(() => {
    devRecordsRef.current = devRecords;
  }, [devRecords]);

  // Reset with the connection: receivedData is cleared on every connect.
  useEffect(() => {
    if (!connected) {
      scanRef.current = 0;
      setFeed([]);
      setPulse([]);
      setBeacon(null);
      setSessionStart(null);
      setArmingInfo(null);
      setEraseProgress(null);
      sessionMetaRef.current = null;
    }
  }, [connected]);

  useEffect(() => {
    if (scanRef.current > receivedData.length) scanRef.current = 0;
    if (receivedData.length <= scanRef.current) return;
    const entries = [];
    const hits = [];
    let lastBeacon = null;
    let sessionEdge; // undefined = none; {to, ts}
    let armingEdge; // undefined = none; null = cleared; object = new phase
    let eraseEdge; // undefined = none; null = done; object = progress
    for (let i = scanRef.current; i < receivedData.length; i++) {
      const { timestamp, data } = receivedData[i];
      const line = String(data);
      if (!line.includes('"t":"')) continue;
      const evs = parseFactoryLines(line);
      for (let k = 0; k < evs.length; k++) {
        const ev = evs[k];
        entries.push({ ev, ts: timestamp, key: `${i}.${k}` });
        const peak = impactPeakG(ev);
        if (peak != null) {
          hits.push(peak);
          if (sessionMetaRef.current && peak > sessionMetaRef.current.peakG) {
            sessionMetaRef.current.peakG = peak;
          }
        }
        if (ev.t === "tx" && ev.pkt === "0x12") {
          lastBeacon = { ts: timestamp, ...ev };
        }
        if (ev.t === "state") {
          sessionEdge = { to: ev.to, from: ev.from, ts: timestamp };
          armingEdge = null; // a state change supersedes any arming phase
        }
        if (ev.t === "sync") {
          if (ev.ev === "erase") {
            // A zero-sector erase (already-empty store) is instant — no bar,
            // and possibly no erase_done to clear one.
            eraseEdge =
              (ev.sectors ?? 0) === 0
                ? null
                : {
                    pct: 0,
                    done: 0,
                    total: ev.sectors ?? null,
                    records: ev.records ?? null,
                    at: timestamp,
                  };
          } else if (ev.ev === "erase_progress") {
            eraseEdge = {
              pct: ev.pct ?? null,
              done: ev.done ?? null,
              total: ev.total ?? null,
              records: null,
              at: timestamp,
            };
          } else if (ev.ev === "erase_done") {
            eraseEdge = null;
          }
        }
        if (ev.t === "session") {
          switch (ev.ev) {
            case "arming":
              armingEdge = {
                phase: "grace",
                secondsLeft: ev.grace_s ?? 60,
                at: timestamp,
              };
              break;
            case "countdown":
              armingEdge = {
                phase: "grace",
                secondsLeft: ev.starts_in_s ?? null,
                at: timestamp,
              };
              break;
            case "hold_link":
              armingEdge = {
                phase: "hold",
                assigned: ev.assigned === true,
                secondsLeft: ev.starts_in_s ?? null,
                at: timestamp,
              };
              break;
            case "sched_wait":
              armingEdge = {
                phase: "sched",
                startsIn: ev.starts_in ?? null,
                startsAt: ev.starts_at ?? null,
                at: timestamp,
              };
              break;
            case "recording":
            case "sched_reached":
            case "ending":
              armingEdge = null;
              break;
            default:
              break;
          }
        }
      }
    }
    scanRef.current = receivedData.length;

    if (entries.length) {
      setFeed((prev) => {
        const { rows, changed } = foldFeedRows(prev, entries);
        return changed ? rows : prev;
      });
    }
    if (hits.length) setPulse((prev) => [...prev, ...hits].slice(-PULSE_CAP));
    if (lastBeacon) setBeacon(lastBeacon);
    if (armingEdge !== undefined) setArmingInfo(armingEdge);
    if (eraseEdge !== undefined) setEraseProgress(eraseEdge);
    if (sessionEdge !== undefined) {
      if (sessionEdge.to === "SESSION") {
        setSessionStart(sessionEdge.ts);
        setSessionSummary(null);
        sessionMetaRef.current = {
          start: sessionEdge.ts,
          startRecords: devRecordsRef.current ?? null,
          peakG: 0,
        };
      } else {
        setSessionStart(null);
        // Only a session that ran through this connection gets a summary —
        // the point is closing the loop on what just happened.
        if (sessionEdge.from === "SESSION" && sessionMetaRef.current) {
          const meta = sessionMetaRef.current;
          sessionMetaRef.current = null;
          setSessionSummary({
            endedAt: sessionEdge.ts,
            durationMs: Math.max(0, sessionEdge.ts - meta.start),
            peakG: meta.peakG > 0 ? meta.peakG : null,
            startRecords: meta.startRecords,
          });
        }
      }
    }
  }, [receivedData]);

  // Stale-guard: if erase_done was missed (unplug mid-erase), don't show a
  // frozen bar forever.
  useEffect(() => {
    if (!eraseProgress) return undefined;
    const t = setTimeout(() => setEraseProgress(null), 180000);
    return () => clearTimeout(t);
  }, [eraseProgress]);

  return {
    feed,
    pulse,
    beacon,
    sessionStart,
    sessionSummary,
    armingInfo,
    eraseProgress,
    dismissSessionSummary: () => setSessionSummary(null),
  };
}
